import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { PlatformHttpService } from '../../../services/platform-http.service';
import { HttpParams } from '@angular/common/http';
import {
  AddHarvestRecordResponse,
  PackagingHarvestRecordPage,
  PackagingHarvestRecordDto,
  ReceiveToWarehouseRequest,
  ReceiveToWarehouseResponse
} from '../models';
import { HarvestRecordDto } from '../../harvest-record/models/harvest-record.models';

/**
 * Service for packaging activity operations
 * Handles adding/removing harvest records (pallets) and listing them
 */
@Injectable({
  providedIn: 'root'
})
export class PackagingService {
  private platformHttp = inject(PlatformHttpService);

  /**
   * Add a harvest record (pallet) to packaging activity
   * @param activityId Production activity ID
   * @param harvestRecordId Harvest record ID to add
   * @returns Response with updated total quantity in kg
   */
  addHarvestRecord(activityId: number, harvestRecordId: number): Observable<AddHarvestRecordResponse> {
    return this.platformHttp.post(
      `/production-activity/${activityId}/packaging/harvest-records/${harvestRecordId}`,
      {}
    ) as Observable<AddHarvestRecordResponse>;
  }

  /**
   * Get paginated list of harvest records added to packaging activity
   * @param activityId Production activity ID
   * @param page Page number (0-indexed)
   * @param size Items per page
   * @returns Paginated list of harvest records
   */
  getHarvestRecords(activityId: number, page: number = 0, size: number = 20): Observable<PackagingHarvestRecordPage> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.platformHttp.get(
      `/production-activity/${activityId}/packaging/harvest-records`,
      params
    ) as Observable<PackagingHarvestRecordPage>;
  }

  /**
   * Remove a harvest record from packaging activity
   * @param activityId Production activity ID
   * @param harvestRecordId Harvest record ID to remove
   * @returns Response with updated total quantity in kg
   */
  removeHarvestRecord(activityId: number, harvestRecordId: number): Observable<AddHarvestRecordResponse> {
    return this.platformHttp.delete(
      `/production-activity/${activityId}/packaging/harvest-records/${harvestRecordId}`
    ) as Observable<AddHarvestRecordResponse>;
  }

  /**
   * Get current total quantity for the packaging activity
   * @param activityId Production activity ID
   * @returns Response with total quantity in kg
   */
  getTotalQuantity(activityId: number): Observable<AddHarvestRecordResponse> {
    return this.platformHttp.get(
      `/production-activity/${activityId}/packaging/total`
    ) as Observable<AddHarvestRecordResponse>;
  }

  /**
   * STEP 1: Create Handling Unit for active packaging activity
   * @param request Request with quantity, boxes, class
   * @returns Response with SSCC and HU details for label printing
   */
  createHandlingUnitStep1(request: ReceiveToWarehouseRequest): Observable<ReceiveToWarehouseResponse> {
    return this.platformHttp.post(
      '/api/v1/packaging/receive-to-warehouse/step1-create-hu',
      request
    ) as Observable<ReceiveToWarehouseResponse>;
  }

  /**
   * STEP 2: Confirm receipt by scanning SSCC or using handlingUnitId
   * @param sscc SSCC (18 digits) from scanned label (optional if handlingUnitId is provided)
   * @param handlingUnitId ID of the handling unit (optional if sscc is provided)
   * @returns Response confirming receipt
   */
  confirmReceiptStep2(sscc: string | null, handlingUnitId?: number): Observable<ReceiveToWarehouseResponse> {
    let params = new HttpParams();
    if (sscc) {
      params = params.set('sscc', sscc);
    }
    if (handlingUnitId) {
      params = params.set('handlingUnitId', handlingUnitId.toString());
    }
    return this.platformHttp.post(
      '/api/v1/packaging/receive-to-warehouse/step2-confirm-receipt',
      {},
      params
    ) as Observable<ReceiveToWarehouseResponse>;
  }

  /**
   * Search harvest records for autocomplete
   * @param searchTerm Search term (ID, variety, batch, etc.)
   * @returns Observable of harvest records
   */
  searchHarvestRecords(searchTerm: string): Observable<HarvestRecordDto[]> {
    const params = new HttpParams()
      .set('search', searchTerm)
      .set('size', '10'); // Limit to 10 results for autocomplete

    return this.platformHttp.get(
      `/harvest-record/search`,
      params
    ) as Observable<HarvestRecordDto[]>;
  }
}

